import {
  ENROLLMENT_ROLE_FACILITATOR,
  ENROLLMENT_ROLE_TEACHER,
  ROLE_TYPE_OFFERING
} from "@pathwright/web/src/modules/invitation/constants"
import { getCohortSectionUrlValues } from "@pathwright/web/src/modules/utils/urls"
import {
  OFFERING_OPTION_IN_PLAN,
  OFFERING_OPTION_NOT_IN_PLAN,
  REGISTER_STATE_AUTH,
  REGISTER_STATE_OFFERING_LIST,
  REGISTER_STATE_OFFERING_OPTIONS,
  REGISTER_STATE_PAYMENT,
  REGISTER_STATE_REGISTERABLE,
  REGISTER_STATE_REGISTERED,
  REGISTER_STATE_REGISTRATION_CLOSED,
  REGISTER_STATE_SUBSCRIBE,
  REGISTER_STATE_UNUSABLE_SUBSCRIPTION
} from "library/register/constants"

const Registration = require("library/models/registration").default
const Inivation = require("library/models/invitation").default
const Discount = require("library/models/discount").default

class RegisterStore extends require("lib/static-shim").default(
  require("lib/core/store/backbone-store").default
) {
  static initClass() {
    this.prototype.storeTriggers = {
      "registration:created"() {
        return {
          registration: this.get("registration"),
          offering: this.get("offering")
        }
      }
    }

    this.prototype.storeTriggerHandlers = {
      subscribe: {
        subscribed(subscription) {
          this._mutate({ subscribed: true })
        },
        "change:subscribe_state"() {
          const { offering_option, state } = this.getState()
          if (state === REGISTER_STATE_SUBSCRIBE) {
            // user signed in inside subscribe flow and they have an active subscription
            if (offering_option === OFFERING_OPTION_IN_PLAN) {
              return this.action.setPrice().setRegisterState()
            }
          }
        },
        "change:plan"() {
          if (this.get("state")) {
            // ensure register store is active
            const subscription_plan = this.getStore("subscribe").get("plan")
            return this.action.mutate({ subscription_plan }).setPrice()
          }
        }
      },
      auth: {
        "user:signed:in"() {
          if (this.get("state")) {
            // ensure register store is active
            this.action.checkUserRegistration(true)
          }
        },
        "user:signed:out"() {
          return this.reset()
        }
      }
    }

    this.prototype.storeEvents = {
      "change:registration_saving"() {
        if (this.get("registration_saving")) {
          window.App.getStore("navigation").action.preventModalClose()
        }
      },
      "change:registration_loading"() {
        if (this.get("registration_loading")) {
          window.App.getStore("navigation").action.preventModalClose()
        }
      }
    }

    this.prototype.resources = {
      invitation: {
        model: Inivation
      },
      discount: {
        model: Discount
      },
      registration: {
        model: Registration
      }
    }

    this.prototype.actions = {
      setOffering(offeringId, offeringOption, inviteToken, visibilityCodeObj) {
        const { registration: registerRegistration } = this.getState()
        const { resource, registration: resourceRegistration } =
          this.getStore("resource").getState()
        const registration = registerRegistration || resourceRegistration

        if (
          registration &&
          registration.offering &&
          registration.offering.id === offeringId
        ) {
          // already registered, navigate to the path
          return window.App.navigate(
            this.request.accessURL(resource, registration)
          )
        }

        const promises = []
        // Load hidden subscriptino plans
        if (visibilityCodeObj) {
          promises.push(
            window.App.getStore("subscribe").action.loadPlans(
              visibilityCodeObj,
              true
            ).promise
          )
        }

        // Load invitation
        if (inviteToken) {
          promises.push(this.action.loadInviteByToken(inviteToken).promise)
        }

        Promise.all(promises).then(async () => {
          const { invitation } = this.getState()

          const offering = invitation
            ? invitation.offering
            : window.App.getStore("resource").request.getOfferingByID(
                offeringId
              )

          this._mutate({
            resource,
            offering: offering || null
          })

          if (offering) {
            await this.action.checkUserRegistration().promise

            const includedInGroupSubscription = await this.getStore(
              "resource"
            ).request.includedInUserGroupSubscription(offering)
            this._mutate({
              includedInGroupSubscription
            })
          }

          this.action
            .setOfferingOption(offeringOption)
            .setPrice()
            .setRegisterState()
        })
      },

      setRegisterStateDirectly(state) {
        return this._mutate({ state })
      },

      setRegisterState() {
        let nextRegistrationState = this.request.getNextRegisterState()

        if (nextRegistrationState === REGISTER_STATE_REGISTERABLE) {
          // let's show auth as register flow does not handle registerable state
          nextRegistrationState = REGISTER_STATE_AUTH
        }

        this._mutate({ state: nextRegistrationState })
      },

      loadUserRegistration(resourceLoaderOptions) {
        const { resource, offering } = this.getState()
        return this.resource.registration.load(
          { resource, offering },
          resourceLoaderOptions
        )
      },

      checkUserRegistration(resourceLoaderOptions) {
        if (!this.getStore("auth").request.isAuthenticated()) {
          return Promise.resolve()
        }
        return this.action
          .loadUserRegistration(resourceLoaderOptions)
          .promise.then(() => {
            const { registration, invitation } = this.getState()
            if (registration) {
              if (registration.path) {
                this._mutate({ already_registered: true })
                if (invitation && invitation.role > registration.role) {
                  // TODO: update registration role? Currently PATCH not allowed on registration
                } else {
                  const { resource } = this.getState()
                  // Assuming user signed in. TODO: handle reloading better way?
                  window.App.getStore("resource")
                    .action.reload(true)
                    .promise.then(() =>
                      // Navigate to the path since already registered
                      window.App.navigate(
                        this.request.accessURL(resource, registration)
                      )
                    )
                }
              }
            } else {
              // Hack: prevent registration_error from showing in UI
              return this._mutate({ registration_error: null })
            }
          })
      },

      loadDiscount(code) {
        const { resource, offering } = this.getState()
        const params = {
          discount_code: code,
          resource_id: resource.id,
          offering_id: offering.id
        }

        return this.resource.discount
          .load(params, true /* forceReload */)
          .then(() => {
            const { discount } = this.getState()
            if (discount) {
              this.action.setPrice()
              return discount
            }
          })
      },

      clearDiscount() {
        this.resource.discount.clearCache()
        this._mutate(this.resource.discount.initialState)
        this.action.setPrice()
      },

      clearDiscountMessage() {
        return this._mutate({ discount_message: null })
      },

      clearRegisterErrors() {
        return this._mutate({
          registration_error: null,
          discount_error: null,
          invitation_error: null
        })
      },

      loadInviteByToken(invite_token) {
        return this.resource.invitation.load({
          invite_token,
          invitation_type: ROLE_TYPE_OFFERING
        })
      },

      setResource(resource) {
        if (resource.id !== this.get("resource")?.id) {
          this._mutate({ resource })
        }

        // Clear registration if necessary
        const registration = this.get("registration")
        if (registration && !registration.get("resource") === resource.id) {
          return this._mutate({ registration: null })
        }
      },

      setOfferingOption(offering_option = null) {
        this._mutate({
          offering_option: this.request.getOfferingOption(offering_option)
        })
      },

      // Set price to null if no payment is required or "can be required" at this point
      setPrice() {
        const { offering, discount, subscription_plan } = this.getState()

        const price = this.request.getPrice()

        let display_price = null
        if (price != null) {
          display_price = discount
            ? discount.discounted_total_currency_display
            : offering != null
            ? offering.currency_display
            : undefined
        } else if (subscription_plan != null) {
          display_price = subscription_plan.currency_display
        }

        this._mutate({ price, display_price })
      },

      setInviteEmails() {
        const invite_emails = this.get("offering").get("invite_emails")
        if (invite_emails) {
          return this._mutate(invite_emails)
        }
      }
    }

    this.prototype.requests = {
      getNextRegisterState(data = {}) {
        const storeState = {
          ...this.getState(),
          ...data
        }
        const {
          registration,
          offering_option,
          invitation,
          offering,
          includedInGroupSubscription
        } = storeState

        const offeringOption = this.request.getOfferingOption(
          offering_option,
          storeState
        )
        const price = this.request.getPrice(storeState)

        const { subscribe_state } = this.getStore("subscribe").getState()
        const { is_authenticated } = this.getStore("auth").getState()
        const includedInUserSubscription =
          this.getStore("resource").request.includedInUserSubscription(offering)
            .length > 0

        const includedInUserGroupSubscription =
          !!includedInGroupSubscription &&
          includedInGroupSubscription.length > 0
        const includedInSubscription =
          includedInUserSubscription || includedInUserGroupSubscription

        // already registered
        if (registration && registration.id) return REGISTER_STATE_REGISTERED
        // pick an offering
        if (!offering) return REGISTER_STATE_OFFERING_LIST
        // pick an offering optioon
        if (!offeringOption && this._offeringHasMultipleOptions(offering)) {
          return REGISTER_STATE_OFFERING_OPTIONS
        }
        // registration is closed for selected offering/option
        if (!this.request.registrationOpen(offering, invitation))
          return REGISTER_STATE_REGISTRATION_CLOSED
        // not authenticated, must sign in/create password
        if (!is_authenticated) return REGISTER_STATE_AUTH
        // check subscription offering/option where user is not subscsribed
        if (
          offeringOption === OFFERING_OPTION_IN_PLAN &&
          !includedInSubscription &&
          // if user is invited to teacher role, subscription requirement is ignored
          !(invitation && invitation.role === ENROLLMENT_ROLE_TEACHER)
        ) {
          // not included in user's active subscription
          if (subscribe_state === "active") {
            return REGISTER_STATE_UNUSABLE_SUBSCRIPTION
          }
          // user must subscribe for offering/option
          return REGISTER_STATE_SUBSCRIBE
        }
        // the offering/option does not require payment
        if (price === 0 || price === null) return REGISTER_STATE_REGISTERABLE
        // the offering/option requires payment
        return REGISTER_STATE_PAYMENT
      },

      getPrice(data = {}) {
        const {
          offering,
          registration,
          offering_option,
          invitation,
          discount,
          includedInGroupSubscription
        } = { ...this.getState(), ...data }

        let price = discount
          ? discount.discounted_total
          : offering != null
          ? offering.price
          : undefined

        const seats_available = this.getStore(
          "resource"
        ).request.seatsAvailable(offering, invitation)
        // Is offering included in user's subscription?

        const freeWithSubscription =
          this.getStore("resource").request.includedInUserSubscription(offering)
            .length > 0 ||
          (!!includedInGroupSubscription &&
            includedInGroupSubscription.length > 0)

        // If any of these checks are true, the price should be null
        if (!offering) {
          price = null
        }
        if (registration && registration.id) {
          price = null
        }
        if (!offering_option) {
          price = null
        }
        if (offering_option === OFFERING_OPTION_IN_PLAN) {
          price = null
        }
        if (freeWithSubscription) {
          price = null
        }
        if (invitation && invitation.is_pre_payed) {
          price = null
        }
        if (
          invitation &&
          invitation.role &&
          invitation.role === ENROLLMENT_ROLE_TEACHER
        ) {
          price = null
        } // no payment required
        // In the case where the price is free and no discount has been applied
        if (price === 0 && !discount) {
          price = null
        }
        if (offering && offering.resource_license && seats_available) {
          price = null
        }

        return price
      },

      getOfferingOption(offering_option, data = {}) {
        const { offering, invitation, includedInGroupSubscription } = {
          ...this.getState(),
          ...data
        }

        if (offering && !offering_option) {
          const seats_available = this.getStore(
            "resource"
          ).request.seatsAvailable(offering, invitation)

          if (invitation && invitation.role === ENROLLMENT_ROLE_TEACHER) {
            offering_option = OFFERING_OPTION_NOT_IN_PLAN
          } else if (
            offering.is_subscription_only ||
            this.getStore("resource").request.includedInUserSubscription(
              offering
            ).length > 0 ||
            (!!includedInGroupSubscription &&
              includedInGroupSubscription.length > 0)
          ) {
            offering_option = OFFERING_OPTION_IN_PLAN
          } else if (!this._offeringHasMultipleOptions(offering)) {
            offering_option = OFFERING_OPTION_NOT_IN_PLAN
          } else if (offering.resource_license && seats_available) {
            offering_option = OFFERING_OPTION_NOT_IN_PLAN
          }
        }

        return offering_option
      },

      onCloseRegisterFlow() {
        const { invitation } = this.getState()
        const { goHome, exitModal } = this.getStore("navigation").action
        let isNotPublic = true
        if (invitation) {
          isNotPublic = invitation.offering.visibility !== 15
        }
        // Modal redirects to dashboard or library if invitation and offering is not public
        if (invitation && isNotPublic) {
          return goHome()
        } else {
          return exitModal()
        }
      },

      // Either return a staff member or the invitation sender, preferring the staff member
      offeringStaffContact() {
        let contact
        const { invitation } = this.getState()
        const { facilitators } = this.getStore("member").getState()

        if (!invitation || !facilitators) {
          return null
        }

        if (facilitators) {
          let staff
          if (invitation && invitation.sender) {
            for (staff of Array.from(facilitators)) {
              if (staff.user.id === invitation.sender.id) {
                contact = staff
              }
            }
          }
          if (!contact) {
            for (staff of Array.from(facilitators)) {
              if (
                contact != null
                  ? staff.role > (contact != null ? contact.role : undefined)
                  : staff
              ) {
                contact = staff
              }
            }
          }
        }

        if (!contact && invitation) {
          contact = invitation.sender
        }

        // Info from Zapier invite has `extra` data
        // if not contact and invitation and invitation.extra
        //   contact =
        //     full_name: "#{invitation.extra.first_name} #{invitation.extra.last_name}"
        //     email: invitation.extra.to_email

        if (contact != null ? contact.user : undefined) {
          contact = contact.user
        }

        return contact
      },

      displayStatus() {
        const { display_price, subscription_plan, offering } = this.getState()
        const displayStatus = { price: display_price }
        if (subscription_plan) {
          _.extend(displayStatus, {
            included: subscription_plan.resources_count,
            interval: (() => {
              switch (subscription_plan.plan.interval) {
                case 5:
                  return "/month"
                case 10:
                  return "/year"
              }
            })()
          })
        }
        return displayStatus
      },

      registrationOpen(offering, invitation) {
        let registration_open = true
        const { is_authenticated } = this.getStore("auth").getState()
        if (offering) {
          registration_open = false
          if (offering.access) {
            const { has_registration_started, has_registration_ended } =
              offering.access
            const seatsAvailable = window.App.getStore(
              "resource"
            ).request.seatsAvailable(offering, invitation)
            const isFull = !(seatsAvailable === -1 || seatsAvailable > 0)
            const windowIsOpen =
              has_registration_started && !has_registration_ended

            let seatLimitExempt =
              !!invitation && invitation.role >= ENROLLMENT_ROLE_FACILITATOR
            let enrollmentWindowExempt =
              !!invitation && invitation.role >= ENROLLMENT_ROLE_FACILITATOR

            // Licensed courses (not from Belltower) are exempt from seat count for now
            if (
              offering.resource_license &&
              offering.resource_license.is_school_licensing &&
              !offering.resource_license.is_belltower_school_licensing
            ) {
              seatLimitExempt = true
              // Othwerwise, licensed offerings are not exempt from seat and enrollment limitations
            } else if (offering.resource_license) {
              seatLimitExempt = false
              enrollmentWindowExempt = false
            }

            if (
              (seatLimitExempt || !isFull) &&
              (enrollmentWindowExempt || windowIsOpen)
            ) {
              registration_open = true
            }
          } else {
            registration_open = true // assuming group offering
          }
        }
        if (
          (invitation != null ? invitation.offering_registration : undefined) &&
          is_authenticated
        ) {
          registration_open = false
        }
        return registration_open
      },

      accessURL(resource, registration) {
        const navStore = this.getStore("navigation")
        // First see if we have a valid path url we can return the user to.
        if (navStore.request.matches("/path/", true /* ignoreModal */)) {
          const modalLanchedFromUrl = navStore.get("modal_launched_from_url")
          const urlValues = getCohortSectionUrlValues(modalLanchedFromUrl)
          // Ensure the modal was launched from the same resource/cohort for which the user is registered.
          if (
            urlValues.resourceId === resource.id &&
            urlValues.cohortId === registration.offering.id
          ) {
            return modalLanchedFromUrl
          }
        }

        return `/library/${resource.slug}/${registration.offering.id}/path/`
      }
    }
  }

  defaults() {
    return {
      registration: null, // the user's registration for the offering (if any)
      offering: null, // the selected offering
      resource: null, // the resource being registered to
      invite_emails: null, // invite emails provided for group offering registration
      subscription_plan: null, // the selected subscription plan
      registering_outside_of_flow: false, // enables registering outside of register flow
      includedInGroupSubscription: [],

      // UI states
      state: null,
      offering_option: null, // in-plan or not-in-plan, meaning option is not included in a plan
      price: null,
      display_price: null, // price with dollar sign
      subscribed: false,
      already_registered: false
    }
  }

  // Determine if the offering is offered in at least one subscription plan and is not subscription only
  _offeringHasMultipleOptions(offering) {
    return (
      !!(
        offering.include_in_subscription_plans.length > 0 &&
        !offering.is_subscription_only &&
        offering.price
      ) ||
      !!(
        offering.include_with_products.length > 0 &&
        !offering.is_subscription_only &&
        offering.price
      )
    )
  }
}
RegisterStore.initClass()

export default window.App.stores.registerStore("register", RegisterStore)
